// Path: src/components/Header.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import img1 from "../assets/nav.png";
import styled from "styled-components";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { Hamburger } from "../icons/Button";
import "./Header.css";
import { CircularProgress } from "@mui/material";
import { api } from "../utils/api";
import { Link as RouterLink, useNavigate } from "react-router-dom";

/* ---------------- Cloudinary helpers (logo) ---------------- */
const PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><rect width='100%' height='100%' fill='%23eef2ff'/></svg>";

function optimizeLogo(url, w = 96) {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("res.cloudinary.com")) return url;
    return url.replace(
      "/upload/",
      `/upload/e_trim,c_fill,g_auto,f_auto,q_auto,dpr_auto,ar_1:1,w_${w},h_${w}/`
    );
  } catch {
    return url;
  }
}

const RAW_LOGO_URL =
  "https://res.cloudinary.com/dlyyvliuh/image/upload/v1755883008/Shivansh_Pdf-1-Picsart-BackgroundRemover_bvedhg.png";
const LOGO_96 = optimizeLogo(RAW_LOGO_URL, 96);
const LOGO_128 = optimizeLogo(RAW_LOGO_URL, 128);
const LOGO_192 = optimizeLogo(RAW_LOGO_URL, 192);

/* ---------------------- Constants ---------------------- */
const SITE = "https://shivanshmahasabha.in";
const NAV_LINKS = [
  { name: "Home", to: "/" },
  { name: "About Us", to: "/about-shivanshmahasabha" },
  { name: "Membership", to: "/membership" },
  { name: "Gallery", to: "/gallery" },
  { name: "Donations", to: "/donations/make-a-change" },
  { name: "Privacy Policy", to: "/privacy-policy" },
];

// JSON-LD SiteNavigationElement (helps discovery of key pages)
const navJsonLd = NAV_LINKS.map((l) => ({
  "@type": "SiteNavigationElement",
  name: l.name,
  url: `${SITE}${l.to === "/" ? "" : l.to}`,
}));

/* ------------------ Styled SPA link ------------------ */
const NavA = styled(RouterLink)`
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  color: #ffffff;
  cursor: pointer;
  transition: color 0.3s ease;
  text-decoration: none;

  &:hover {
    color: #f9b806;
  }
`;

export default function Header() {
  const [showNav, setShowNav] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const ddRef = useRef(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  const firstMobileLinkRef = useRef(null);

  const closeAll = useCallback(() => {
    setShowDropDown(false);
    setShowNav(false);
  }, []);

  const onDocClick = useCallback((e) => {
    if (!ddRef.current) return;
    if (!ddRef.current.contains(e.target)) setShowDropDown(false);
  }, []);

  const onEsc = useCallback(
    (e) => {
      if (e.key === "Escape") closeAll();
    },
    [closeAll]
  );

  useEffect(() => {
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onDocClick, onEsc]);

  /* Focus first link when mobile menu opens (basic a11y) */
  useEffect(() => {
    if (showNav && firstMobileLinkRef.current) {
      firstMobileLinkRef.current.focus();
    }
  }, [showNav]);

  /* Fetch newsletters (abortable) */
  useEffect(() => {
    (async () => {
      setLoading(true);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const items = await api.newsletter.news({ signal: controller.signal, retry: 1 });
        const normalized = (Array.isArray(items) ? items : []).map((it) => ({
          title:
            it?.title ||
            it?.context?.custom?.title ||
            (it?.public_id ? it.public_id.split("/").pop().replace(/[-_]/g, " ") : "Newsletter"),
          url: it?.documentLink || it?.url || it?.secure_url || "#",
        }));
        setNewsletters(normalized);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Error fetching newsletters:", err);
          setNewsletters([]);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => abortRef.current?.abort();
  }, []);

  // Build absolute URLs once for JSON-LD and prefetch cues
  const navAbs = useMemo(
    () => NAV_LINKS.map((l) => ({ ...l, abs: `${SITE}${l.to === "/" ? "" : l.to}` })),
    []
  );

  return (
    <div className="relative z-[90] h-[100px] lg:h-[135.03px]">
      <Helmet>
        {/* Site-wide signals */}
        <meta name="official-whatsapp-number" content="+917895345056" />
        <script type="application/ld+json">
          {JSON.stringify([{ "@context": "https://schema.org", ...navJsonLd[0] }, ...navJsonLd.slice(1)])}
        </script>

        {/* Performance */}
        <link rel="preconnect" href="https://res.cloudinary.com" crossOrigin="anonymous" />
        <link
          rel="preload"
          as="image"
          href={LOGO_128}
          imagesrcset={`${LOGO_96} 96w, ${LOGO_128} 128w, ${LOGO_192} 192w`}
          imagesizes="(min-width:1024px) 75px, 80px"
        />

        {/* Soft SPA hints (browsers may or may not prefetch these) */}
        {navAbs
          .filter((l) => l.to !== "/")
          .map((l) => (
            <link key={l.to} rel="prefetch" href={l.to} />
          ))}
      </Helmet>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] bg-white text-[#214895] px-3 py-2 rounded"
      >
        Skip to main content
      </a>

      {/* Desktop */}
      <div className="fixed bg-gradient-to-br from-[#214895] via-[#204896] to-[#18367e] lg:flex hidden px-20 shadow-xl py-5 gap-10 w-full border-b-2 border-[#f9b806]">
        <button
          type="button"
          onClick={() => {
            closeAll();
            navigate("/");
          }}
          className="logo hover:scale-105 transition-transform duration-300 focus:outline-none"
          aria-label="Go to home"
        >
          <img
            src={LOGO_128}
            srcSet={`${LOGO_96} 96w, ${LOGO_128} 128w, ${LOGO_192} 192w`}
            sizes="75px"
            className="h-[75px] w-[75px] border-2 border-[#f9b806] rounded-full"
            alt="Shivansh Mahasabha logo"
            width={75}
            height={75}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
          />
        </button>

        <nav aria-label="Primary" className="flex items-end gap-7 pb-7">
          <ul className="flex items-end list-none gap-7 m-0 p-0">
            <li><NavA to="/" onClick={closeAll}>Home</NavA></li>
            <li><NavA to="/about-shivanshmahasabha" onClick={closeAll}>About Us</NavA></li>
            <li><NavA to="/membership" onClick={closeAll}>Membership</NavA></li>

            <li className="relative" ref={ddRef}>
              <button
                type="button"
                className="flex items-center gap-1 text-white uppercase font-bold text-[13px] hover:text-[#f9b806] transition-colors"
                onClick={() => setShowDropDown((s) => !s)}
                onMouseEnter={() => setShowDropDown(true)}
                onFocus={() => setShowDropDown(true)}
                aria-expanded={showDropDown ? "true" : "false"}
                aria-haspopup="menu"
                aria-controls="desktop-newsletters"
              >
                Newsletters {showDropDown ? <ArrowDropUp /> : <ArrowDropDown />}
              </button>

              <div
                id="desktop-newsletters"
                className={`${showDropDown ? "absolute" : "hidden"} pt-[76px] w-56 top-0`}
                role="menu"
                aria-label="Newsletters"
                onMouseLeave={() => setShowDropDown(false)}
              >
                <div className="bg-gradient-to-b from-[#214895] to-[#18367e] px-5 py-3 w-56 top-[75px] flex flex-col gap-2 border-2 border-[#f9b806]/50 rounded-lg shadow-xl">
                  {!loading ? (
                    newsletters.length ? (
                      newsletters.map((item, idx) => (
                        <a
                          key={idx}
                          className="text-white hover:text-[#f9b806] cursor-pointer text-sm py-1 border-b border-[#ffffff15] last:border-0"
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          role="menuitem"
                          onClick={closeAll}
                        >
                          {item.title}
                        </a>
                      ))
                    ) : (
                      <span className="text-white/70 text-sm">No newsletters yet</span>
                    )
                  ) : (
                    <CircularProgress size={20} sx={{ color: "#f9b806" }} role="status" aria-label="Loading newsletters" />
                  )}
                </div>
              </div>
            </li>

            <li><NavA to="/gallery" onClick={closeAll}>Gallery</NavA></li>
            <li><NavA to="/donations/make-a-change" onClick={closeAll}>Donations</NavA></li>
            <li><NavA to="/privacy-policy" onClick={closeAll}>Privacy Policy</NavA></li>
          </ul>
        </nav>

        <div className="absolute right-20 flex items-end gap-10">
          <button
            onClick={() => {
              closeAll();
              navigate("/donations/make-a-change");
            }}
            className="bg-gradient-to-r from-[#f9b806] to-[#e0a905] h-10 px-6 mb-4 text-[#214895] font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:from-[#e0a905] hover:to-[#f9b806] border-2 border-white/30"
            aria-label="Donate now"
          >
            Donate Now
          </button>
          <img
            className="ute hover:scale-110 transition-transform duration-300"
            src={img1}
            height={40}
            width={50}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      {/* Mobile */}
      <div className="shadow-md lg:hidden z-[90] fixed w-full top-0 bg-gradient-to-br from-[#214895] to-[#18367e] flex justify-between px-4 items-center py-3 border-b-2 border-[#f9b806]">
        <button
          type="button"
          className="logo focus:outline-none"
          onClick={() => {
            closeAll();
            navigate("/");
          }}
          aria-label="Go to home"
        >
          <img
            src={LOGO_128}
            srcSet={`${LOGO_96} 96w, ${LOGO_128} 128w, ${LOGO_192} 192w`}
            sizes="80px"
            className="h-[80px] w-[80px] border-2 border-[#f9b806] rounded-full"
            alt="Shivansh Mahasabha logo"
            width={80}
            height={80}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
          />
        </button>

        <div className="btns">
          <Hamburger showNav={showNav} setShowNav={setShowNav} />
        </div>

        <div
          className={`absolute top-24 overflow-hidden bg-gradient-to-b from-[#214895] to-[#18367e] w-full left-0 ${
            showNav ? "showMenu" : "hideMenu"
          } border-t-2 border-[#f9b806]`}
          role="dialog"
          aria-label="Mobile navigation"
          aria-modal="true"
        >
          <ul className="flex flex-col items-center list-none gap-7 py-9 m-0">
            <li>
              <NavA to="/" onClick={closeAll} ref={firstMobileLinkRef}>
                Home
              </NavA>
            </li>
            <li><NavA to="/about-shivanshmahasabha" onClick={closeAll}>About Us</NavA></li>
            <li><NavA to="/membership" onClick={closeAll}>Membership</NavA></li>

            <li className="w-full">
              <button
                type="button"
                onClick={() => setShowDropDown((s) => !s)}
                className="relative mr-2 flex items-center gap-1 text-white uppercase font-bold text-[13px] hover:text-[#f9b806] transition-colors w-full justify-center"
                aria-expanded={showDropDown ? "true" : "false"}
                aria-controls="mobile-newsletters"
              >
                Newsletters {showDropDown ? <ArrowDropUp /> : <ArrowDropDown />}
              </button>
              <ul
                id="mobile-newsletters"
                className={`${showDropDown ? "h-auto" : "hidden"} overflow-hidden list-none flex flex-col items-center pt-2 gap-2 w-full px-4`}
                role="menu"
                aria-label="Newsletters"
              >
                {!loading ? (
                  newsletters.length ? (
                    newsletters.map((item, idx) => (
                      <li key={idx} className="w-full text-center">
                        <a
                          className="text-white hover:text-[#f9b806] cursor-pointer text-sm py-2 w-full inline-block border-b border-[#ffffff15] last:border-0"
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeAll}
                          role="menuitem"
                        >
                          {item.title}
                        </a>
                      </li>
                    ))
                  ) : (
                    <span className="text-white/70 text-sm">No newsletters yet</span>
                  )
                ) : (
                  <CircularProgress size={20} sx={{ color: "#f9b806" }} role="status" aria-label="Loading newsletters" />
                )}
              </ul>
            </li>

            <li><NavA to="/gallery" onClick={closeAll}>Gallery</NavA></li>
            <li><NavA to="/donations/make-a-change" onClick={closeAll}>Donations</NavA></li>
            <li><NavA to="/privacy-policy" onClick={closeAll}>Privacy Policy</NavA></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
